import { Component, OnInit } from '@angular/core'
import { Firestore, collection, doc, setDoc, getDoc, getDocs, Timestamp, query, orderBy, collectionGroup } from '@angular/fire/firestore'
import { AuthenticationService } from 'src/app/services/authentication.service'
import { BackendService } from 'src/app/services/backend.service'
import { ENTERPRISE_CONFIGS, EnterpriseConfig, getAllEnterpriseConfigs } from 'src/app/config/enterprise.config'
import { Merchant, merchantsPath, FirestoreStructure } from '@parkupp/core'
import Swal from 'sweetalert2'

interface EnterpriseDocument {
    $key: string
    name: string
    domain: string
    active: boolean
    createdAt: any
    updatedAt: any
}

interface EnterpriseManagerDocument {
    uid: string
    enterpriseKey: string
    active: boolean
    createdAt: any
    updatedAt: any
}

interface DataIntegrityCheck {
    collection: string
    displayName: string
    totalCount: number
    missingCount: number
    loading: boolean
    error?: string
}

@Component({
    selector: 'app-admin-enterprise-management',
    templateUrl: './admin-enterprise-management.component.html',
    styleUrls: ['./admin-enterprise-management.component.scss'],
})
export class AdminEnterpriseManagementComponent implements OnInit {
    // UI State
    activeStep = 1
    loading = false

    // Enterprise Data
    configuredEnterprises: EnterpriseConfig[] = []
    firestoreEnterprises: EnterpriseDocument[] = []
    enterpriseManagers: EnterpriseManagerDocument[] = []

    // Form data for new enterprise
    newEnterprise = {
        key: '',
        name: '',
        domain: '',
    }

    // Form data for new enterprise manager
    newManager = {
        uid: '',
        enterpriseKey: '',
    }

    // Migration status
    migrationStatus = {
        running: false,
        merchantsCount: 0,
        parkingsCount: 0,
        subscriptionsCount: 0,
        invoicesCount: 0,
        lastRun: null as Date | null,
    }

    // Assign portfolio form
    assignPortfolio = {
        portfolioKey: '',
        enterpriseKey: '',
    }

    // Portfolio list for searchable dropdown
    portfolios: { $key: string; companyName: string; denormEnterpriseKey?: string }[] = []

    // Data Integrity checks
    integrityChecks: DataIntegrityCheck[] = []
    integrityLoading = false
    integrityLastChecked: Date | null = null

    constructor(private firestore: Firestore, public authService: AuthenticationService, private backendService: BackendService) {}

    ngOnInit(): void {
        this.loadConfiguredEnterprises()
        this.loadFirestoreEnterprises()
        this.loadEnterpriseManagers()
        this.loadPortfolios()
    }

    // ===== STEP NAVIGATION =====
    setStep(step: number) {
        this.activeStep = step
    }

    // ===== LOAD DATA =====
    loadConfiguredEnterprises() {
        this.configuredEnterprises = getAllEnterpriseConfigs()
    }

    async loadFirestoreEnterprises() {
        try {
            const enterprisesRef = collection(this.firestore, 'enterprises')
            const snapshot = await getDocs(enterprisesRef)
            this.firestoreEnterprises = snapshot.docs.map((doc) => ({
                $key: doc.id,
                ...doc.data(),
            })) as EnterpriseDocument[]
        } catch (error) {
            console.error('Error loading enterprises:', error)
        }
    }

    async loadEnterpriseManagers() {
        try {
            const managersRef = collection(this.firestore, 'enterpriseManagers')
            const snapshot = await getDocs(managersRef)
            this.enterpriseManagers = snapshot.docs.map((doc) => ({
                uid: doc.id,
                ...doc.data(),
            })) as EnterpriseManagerDocument[]
        } catch (error) {
            console.error('Error loading enterprise managers:', error)
        }
    }

    async loadPortfolios() {
        try {
            const merchantsRef = collection(this.firestore, merchantsPath())
            const q = query(merchantsRef, orderBy('companyName', 'asc'))
            const snapshot = await getDocs(q)
            this.portfolios = snapshot.docs.map((doc) => ({
                $key: doc.id,
                companyName: doc.data()['companyName'] || 'Unnamed Portfolio',
                denormEnterpriseKey: doc.data()['denormEnterpriseKey'],
            }))
        } catch (error) {
            console.error('Error loading portfolios:', error)
        }
    }

    // ===== CREATE ENTERPRISE =====
    async createEnterpriseInFirestore(enterpriseKey: string) {
        const config = ENTERPRISE_CONFIGS[enterpriseKey]
        if (!config) {
            await Swal.fire('Error', `No config found for enterprise key: ${enterpriseKey}`, 'error')
            return
        }

        const confirmResult = await Swal.fire({
            title: 'Create Enterprise?',
            html: `
                <p>This will create the following enterprise in Firestore:</p>
                <ul class="text-start">
                    <li><strong>Key:</strong> ${config.key}</li>
                    <li><strong>Name:</strong> ${config.name}</li>
                    <li><strong>Domain:</strong> ${config.domain}</li>
                </ul>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Create Enterprise',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        this.loading = true
        try {
            const enterpriseRef = doc(this.firestore, 'enterprises', config.key)
            await setDoc(enterpriseRef, {
                name: config.name,
                domain: config.domain,
                active: true,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            })

            await Swal.fire('Success!', `Enterprise "${config.name}" created successfully.`, 'success')
            await this.loadFirestoreEnterprises()
        } catch (error) {
            console.error('Error creating enterprise:', error)
            await Swal.fire('Error', 'Failed to create enterprise. Check console for details.', 'error')
        } finally {
            this.loading = false
        }
    }

    async createCustomEnterprise() {
        if (!this.newEnterprise.key || !this.newEnterprise.name || !this.newEnterprise.domain) {
            await Swal.fire('Error', 'Please fill in all fields.', 'error')
            return
        }

        const confirmResult = await Swal.fire({
            title: 'Create Custom Enterprise?',
            html: `
                <p>This will create a new enterprise in Firestore:</p>
                <ul class="text-start">
                    <li><strong>Key:</strong> ${this.newEnterprise.key}</li>
                    <li><strong>Name:</strong> ${this.newEnterprise.name}</li>
                    <li><strong>Domain:</strong> ${this.newEnterprise.domain}</li>
                </ul>
                <p class="text-warning mt-3"><i class="fa fa-warning"></i> You'll also need to add this to the enterprise.config.ts files!</p>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Create Enterprise',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        this.loading = true
        try {
            const enterpriseRef = doc(this.firestore, 'enterprises', this.newEnterprise.key)
            await setDoc(enterpriseRef, {
                name: this.newEnterprise.name,
                domain: this.newEnterprise.domain,
                active: true,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            })

            await Swal.fire('Success!', `Enterprise "${this.newEnterprise.name}" created successfully.`, 'success')
            this.newEnterprise = { key: '', name: '', domain: '' }
            await this.loadFirestoreEnterprises()
        } catch (error) {
            console.error('Error creating enterprise:', error)
            await Swal.fire('Error', 'Failed to create enterprise. Check console for details.', 'error')
        } finally {
            this.loading = false
        }
    }

    // ===== CREATE ENTERPRISE MANAGER =====
    async createEnterpriseManager() {
        if (!this.newManager.uid || !this.newManager.enterpriseKey) {
            await Swal.fire('Error', 'Please fill in UID and select an enterprise.', 'error')
            return
        }

        const confirmResult = await Swal.fire({
            title: 'Create Enterprise Manager?',
            html: `
                <p>This will grant enterprise manager access:</p>
                <ul class="text-start">
                    <li><strong>User UID:</strong> ${this.newManager.uid}</li>
                    <li><strong>Enterprise:</strong> ${this.newManager.enterpriseKey}</li>
                </ul>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Create Manager',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        this.loading = true
        try {
            const managerRef = doc(this.firestore, 'enterpriseManagers', this.newManager.uid)
            await setDoc(managerRef, {
                uid: this.newManager.uid,
                enterpriseKey: this.newManager.enterpriseKey,
                active: true,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            })

            await Swal.fire('Success!', 'Enterprise manager created successfully.', 'success')
            this.newManager = { uid: '', enterpriseKey: '' }
            await this.loadEnterpriseManagers()
        } catch (error) {
            console.error('Error creating enterprise manager:', error)
            await Swal.fire('Error', 'Failed to create enterprise manager. Check console for details.', 'error')
        } finally {
            this.loading = false
        }
    }

    async toggleManagerActive(manager: EnterpriseManagerDocument) {
        const newStatus = !manager.active
        const confirmResult = await Swal.fire({
            title: newStatus ? 'Activate Manager?' : 'Deactivate Manager?',
            text: `This will ${newStatus ? 'activate' : 'deactivate'} the enterprise manager.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        try {
            const managerRef = doc(this.firestore, 'enterpriseManagers', manager.uid)
            await setDoc(
                managerRef,
                {
                    ...manager,
                    active: newStatus,
                    updatedAt: Timestamp.now(),
                },
                { merge: true }
            )

            await Swal.fire('Success!', `Manager ${newStatus ? 'activated' : 'deactivated'}.`, 'success')
            await this.loadEnterpriseManagers()
        } catch (error) {
            console.error('Error toggling manager:', error)
            await Swal.fire('Error', 'Failed to update manager. Check console for details.', 'error')
        }
    }

    // ===== MIGRATIONS =====
    async runMigration(enterpriseKey: string) {
        const confirmResult = await Swal.fire({
            title: 'Run Migration?',
            html: `
                <p>This will assign all existing data <strong>without an enterprise</strong> to:</p>
                <p><strong>${enterpriseKey}</strong></p>
                <hr>
                <p class="text-muted">Collections affected:</p>
                <ul class="text-start text-muted">
                    <li>Merchants</li>
                    <li>Parkings</li>
                    <li>Subscriptions</li>
                    <li>Invoices</li>
                </ul>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Run Migration',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        this.migrationStatus.running = true
        try {
            const idToken = await this.authService.auth.currentUser?.getIdToken()
            if (!idToken) throw new Error('No auth token')

            const result = await this.backendService.callFunction('migrations-migrateToEnterprise', { enterpriseKey }, idToken)

            this.migrationStatus.merchantsCount = result.merchants || 0
            this.migrationStatus.parkingsCount = result.parkings || 0
            this.migrationStatus.subscriptionsCount = result.subscriptions || 0
            this.migrationStatus.invoicesCount = result.invoices || 0
            this.migrationStatus.lastRun = new Date()

            await Swal.fire({
                title: 'Migration Complete!',
                html: `
                    <ul class="text-start">
                        <li><strong>${result.merchants || 0}</strong> merchants updated</li>
                        <li><strong>${result.parkings || 0}</strong> parkings updated</li>
                        <li><strong>${result.subscriptions || 0}</strong> subscriptions updated</li>
                        <li><strong>${result.invoices || 0}</strong> invoices updated</li>
                    </ul>
                `,
                icon: 'success',
                confirmButtonText: 'OK',
            })
        } catch (error) {
            console.error('Error running migration:', error)
            await Swal.fire('Error', 'Failed to run migration. Check console for details.', 'error')
        } finally {
            this.migrationStatus.running = false
        }
    }

    async assignPortfolioToEnterprise() {
        if (!this.assignPortfolio.portfolioKey || !this.assignPortfolio.enterpriseKey) {
            await Swal.fire('Error', 'Please select a portfolio and an enterprise.', 'error')
            return
        }

        const portfolio = this.portfolios.find((p) => p.$key === this.assignPortfolio.portfolioKey)
        const portfolioName = portfolio?.companyName || this.assignPortfolio.portfolioKey
        const enterpriseName = this.getEnterpriseName(this.assignPortfolio.enterpriseKey)

        const confirmResult = await Swal.fire({
            title: 'Assign Portfolio?',
            html: `
                <p>This will assign portfolio <strong>${portfolioName}</strong> to enterprise <strong>${enterpriseName}</strong>.</p>
                <p class="text-muted">All related parkings, subscriptions, and invoices will also be updated.</p>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Assign',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        this.loading = true
        try {
            const idToken = await this.authService.auth.currentUser?.getIdToken()
            if (!idToken) throw new Error('No auth token')

            const result = await this.backendService.callFunction(
                'migrations-assignMerchantToEnterprise',
                {
                    merchantKey: this.assignPortfolio.portfolioKey,
                    enterpriseKey: this.assignPortfolio.enterpriseKey,
                },
                idToken
            )

            await Swal.fire({
                title: 'Assignment Complete!',
                html: `
                    <ul class="text-start">
                        <li><strong>1</strong> portfolio updated</li>
                        <li><strong>${result.parkings || 0}</strong> parkings updated</li>
                        <li><strong>${result.subscriptions || 0}</strong> subscriptions updated</li>
                        <li><strong>${result.invoices || 0}</strong> invoices updated</li>
                    </ul>
                `,
                icon: 'success',
                confirmButtonText: 'OK',
            })

            this.assignPortfolio = { portfolioKey: '', enterpriseKey: '' }
            await this.loadPortfolios() // Refresh the list
        } catch (error) {
            console.error('Error assigning portfolio:', error)
            await Swal.fire('Error', 'Failed to assign portfolio. Check console for details.', 'error')
        } finally {
            this.loading = false
        }
    }

    // ===== UTILITY =====
    isEnterpriseInFirestore(key: string): boolean {
        return this.firestoreEnterprises.some((e) => e.$key === key)
    }

    getEnterpriseName(key: string): string {
        const config = ENTERPRISE_CONFIGS[key]
        return config ? config.name : key
    }

    copyToClipboard(text: string) {
        navigator.clipboard.writeText(text)
        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: 'Copied!',
            showConfirmButton: false,
            timer: 1500,
        })
    }

    // ===== DATA INTEGRITY =====
    initIntegrityChecks(): void {
        this.integrityChecks = [
            { collection: FirestoreStructure.MERCHANTS, displayName: 'Merchants (Portfolios)', totalCount: 0, missingCount: 0, loading: false },
            // Note: Parkings are subcollections under merchants and inherit enterpriseKey during migration
            // Collection group queries require special security rules, so we skip checking parkings here
            // If merchants are migrated correctly, parkings should be correct
            { collection: FirestoreStructure.SUBSCRIPTIONS, displayName: 'Subscriptions', totalCount: 0, missingCount: 0, loading: false },
            { collection: FirestoreStructure.INVOICES, displayName: 'Invoices', totalCount: 0, missingCount: 0, loading: false },
            { collection: FirestoreStructure.PAYMENTS, displayName: 'Payments', totalCount: 0, missingCount: 0, loading: false },
            { collection: FirestoreStructure.PAYOUTS, displayName: 'Payouts', totalCount: 0, missingCount: 0, loading: false },
            { collection: FirestoreStructure.BOOKINGS, displayName: 'Bookings', totalCount: 0, missingCount: 0, loading: false },
            { collection: FirestoreStructure.LOG_EMAILS, displayName: 'Email Logs', totalCount: 0, missingCount: 0, loading: false },
            { collection: FirestoreStructure.ADMIN_CLIENTS, displayName: 'Admin Clients (CRM)', totalCount: 0, missingCount: 0, loading: false },
            { collection: FirestoreStructure.LEADS, displayName: 'Leads', totalCount: 0, missingCount: 0, loading: false },
            { collection: FirestoreStructure.ENQUIRIES, displayName: 'Enquiries', totalCount: 0, missingCount: 0, loading: false },
        ]
    }

    async runIntegrityCheck(): Promise<void> {
        this.initIntegrityChecks()
        this.integrityLoading = true

        for (const check of this.integrityChecks) {
            check.loading = true
            check.error = undefined

            try {
                if (check.collection === 'parkings') {
                    // Parkings is a subcollection under merchants, use collectionGroup
                    await this.checkCollectionGroup(check)
                } else {
                    await this.checkCollection(check)
                }
            } catch (error: any) {
                console.error(`Error checking ${check.collection}:`, error)
                check.error = error.message || 'Failed to check'
            }

            check.loading = false
        }

        this.integrityLoading = false
        this.integrityLastChecked = new Date()
    }

    private async checkCollection(check: DataIntegrityCheck): Promise<void> {
        const collectionRef = collection(this.firestore, check.collection)

        // Get total count
        const allDocs = await getDocs(collectionRef)
        check.totalCount = allDocs.size

        // Count missing enterprise key
        let missingCount = 0
        allDocs.forEach((doc) => {
            const data = doc.data()
            if (!data['denormEnterpriseKey']) {
                missingCount++
            }
        })
        check.missingCount = missingCount
    }

    private async checkCollectionGroup(check: DataIntegrityCheck): Promise<void> {
        // For subcollections like parkings, use collectionGroup
        const groupRef = collectionGroup(this.firestore, check.collection)

        const allDocs = await getDocs(groupRef)
        check.totalCount = allDocs.size

        let missingCount = 0
        allDocs.forEach((doc) => {
            const data = doc.data()
            if (!data['denormEnterpriseKey']) {
                missingCount++
            }
        })
        check.missingCount = missingCount
    }

    getIntegrityStatus(): 'success' | 'warning' | 'error' {
        const totalMissing = this.integrityChecks.reduce((sum, check) => sum + check.missingCount, 0)
        if (totalMissing === 0) return 'success'
        if (totalMissing < 10) return 'warning'
        return 'error'
    }

    getTotalMissing(): number {
        return this.integrityChecks.reduce((sum, check) => sum + check.missingCount, 0)
    }

    getTotalRecords(): number {
        return this.integrityChecks.reduce((sum, check) => sum + check.totalCount, 0)
    }
}
